import { Button, Callout, Classes, Dialog, FormGroup, Intent, Tag } from '@blueprintjs/core'
import isEmpty from 'lodash.isempty'
import * as React from 'react'
import Client from '../../../client'
import APIErrorCallout from '../../../components/errors/APIErrorCallout'
import NullSafeNumericInput from '../../../components/NullSafeNumericInput'
import ListingDurationSelect from '../../../components/selects/channels/shopify/ListingDurationSelect'
import Spinner from '../../../components/Spinner'
import { showToast } from '../../../Toast'
import { UprightStyle } from '../../../types'
import { S, readQueryParams } from '../../../utils'
import { isShopifyAccountCAD } from '../../../utils/marketAccount'
import config from '../../../config'
import { ScheduleTimePickerRelist } from './RelistDialogScheduleTime'
import { pluralizeFromNumber } from '../../../utils/string'
import { trackNeedsAttentionListingEvent } from '../../home/v2/utils'

type Action = 'relist' | 'list' | 'move'

interface Props {
  action: Action
  marketAccountId: number
  data: any
  recycledProductId?: number
  onClose: (object?: any, failedIds?: any) => void
  onProductRestored?: (data) => void
  onSubmit?: (form, error) => void
  actionTitle?: string
  isOpen: boolean
  aProductHasVariants: boolean
}

interface State {
  error: string
  loading: boolean
  showPriceWarning: boolean
  showCompareAtPriceWarning: boolean
  isPriceAndCompareAtPriceInvalid: boolean
  form: {
    price?: string
    compareAtPrice?: string
    duration?: string
    quantity?: string
    scheduleTime?: string
  }
  useCAD: boolean
}

const styles: UprightStyle = {
  restoreButton: { marginTop: 5 },
  formContainer: { width: '90%' },
  footerActionsContainer: { minWidth: 100 },
  submitButton: { width: '100%' },
  compareAtPriceTextWarning: {
    marginBottom: 0,
    fontSize: 12,
    color: '#D9822B'
  },
  priceTextWarning: {
    marginBottom: 0,
    fontSize: 12,
    color: '#DB3737'
  },
  spacingDiv: {
    height: 36
  }
}

const actionMessagePrefix: { [key in Action]: string } = {
  relist: 'Relisting',
  list: 'Listing',
  move: 'Moving'
}

export default class BulkListDialog extends React.Component<Props, State> {
  state = {
    error: null,
    loading: false,
    showPriceWarning: false,
    showCompareAtPriceWarning: false,
    isPriceAndCompareAtPriceInvalid: false,
    form: {
      price: '',
      compareAtPrice: '',
      duration: null,
      quantity: '',
      scheduleTime: null
    },
    useCAD: false
  }

  onFormChange = (name, value) => {
    this.setState(s => ({
      form: {
        ...s.form,
        [name]: value
      }
    }))
  }

  fetchDefaults = async () => {
    const { marketAccountId } = this.props
    if (!marketAccountId) return

    this.setState({ loading: true })
    try {
      const res = await Client.getShopifySettings(marketAccountId)
      const { settings, account } = res.data
      this.setState({
        loading: false,
        useCAD: isShopifyAccountCAD(account),
        form: {
          price: settings.defaultPrice
        }
      })
    } catch (error) {
      this.setState({
        loading: false,
        error: error.response.data
      })
    }
  }

  restoreProduct = async () => {
    const { data, recycledProductId } = this.props
    try {
      await Client.recycleProduct(recycledProductId, false)
      // This callback isn't meant to be called in bulk, therefor assume an array of 1 will be supplied
      if (this.props.onProductRestored) this.props.onProductRestored(data[0])
    } catch (error) {
      this.setState({ loading: false, error: error.response })
    }
  }

  takeAction = async () => {
    const { data } = this.props
    this.setState({ loading: true, error: null })

    let clientMethod = Client.bulkListShopify
    if (this.props.action === 'relist') {
      clientMethod = Client.bulkRelistShopify
    }

    try {
      const res = await clientMethod(data, this.state.form)
      this.setState({ loading: false }, () => {
        if (readQueryParams().tab === 'attention-required')
          trackNeedsAttentionListingEvent(this.props.action === 'relist' ? 're-list' : 'list', data.length)
        const messageSuffix = `${data.length} item${S(data)}...`
        const message = `${actionMessagePrefix[this.props.action]} ${messageSuffix}`
        showToast(message, Intent.PRIMARY)
        this.props.onClose(true, res.data.failed)
      })
    } catch (error) {
      this.setState({ loading: false, error: error.response })
    }
  }

  maybeCustomSubmit = async () => {
    if (this.props.action === 'move') {
      this.setState({ loading: true, error: null })

      this.props.onSubmit(this.state.form, error => {
        this.setState({ loading: false, error })
        if (!error) this.props.onClose(true)
      })
    } else {
      await this.takeAction()
    }
  }

  validatePriceAndCompareAtPrice = async () => {
    const { form } = this.state

    let showPriceWarning = false
    let showCompareAtPriceWarning = false
    let isInvalid = false

    if (isEmpty(form.price) && !isEmpty(form.compareAtPrice)) {
      // CompareAtPrice set but price is not set. Invalid because price always needs to be less than CompareAtPrice and
      // we cannot guarantee the previous listing price is lower than the newly set compareAtPrice.
      showPriceWarning = true
      isInvalid = true
    } else if (!isEmpty(form.price) && isEmpty(form.compareAtPrice)) {
      // Price set but CompareAtPrice is not set. Valid because we can remove the compare at price option if left blank/
      showCompareAtPriceWarning = true
      isInvalid = false
    } else if (!isEmpty(form.price) && !isEmpty(form.compareAtPrice)) {
      // Both price and compare at price are set. Compare numbers to ensure price is less than compare at price
      const isPriceGreaterOrEqlToCompareAtPrice = +form.price >= +form.compareAtPrice

      isInvalid = isPriceGreaterOrEqlToCompareAtPrice
      showCompareAtPriceWarning = isPriceGreaterOrEqlToCompareAtPrice
    }

    this.setState({
      isPriceAndCompareAtPriceInvalid: isInvalid,
      showPriceWarning,
      showCompareAtPriceWarning
    })
  }

  onClose = () => {
    this.setState(
      {
        loading: false,
        error: null
      },
      this.props.onClose
    )
  }

  onChangeScheduleTimePickerRelist = v => {
    this.onFormChange('scheduleTime', v ? v.value : null)
  }

  render() {
    const { isOpen, data, recycledProductId, aProductHasVariants } = this.props
    const { form, loading, error, useCAD } = this.state

    let helperText = ''
    if (aProductHasVariants) {
      helperText = `Selected product${pluralizeFromNumber(data.length)} ${
        data.length > 1 ? 'have' : 'has'
      } variants. Edit the product${pluralizeFromNumber(data.length)} to change this value.`
    }

    return (
      <Dialog
        onOpening={this.fetchDefaults}
        isOpen={isOpen}
        onClose={this.onClose}
        title={this.props.actionTitle || 'Relist'}
      >
        <div className={Classes.DIALOG_BODY}>
          {error && <APIErrorCallout response={error} />}
          {recycledProductId && (
            <Callout intent={Intent.DANGER}>
              This listing&apos;s product is <strong>recycled</strong>. You must restore it first.
              <br />
              <Button style={styles.restoreButton} small intent={Intent.PRIMARY} onClick={this.restoreProduct}>
                Restore
              </Button>
            </Callout>
          )}
          {loading ? (
            <Spinner />
          ) : (
            <div style={styles.formContainer}>
              <FormGroup label="Price" helperText={helperText}>
                <NullSafeNumericInput
                  min={0}
                  max={1000}
                  minorStepSize={0.01}
                  leftIcon="dollar"
                  stepSize={1}
                  majorStepSize={1}
                  selectAllOnFocus
                  placeholder="Use current"
                  onValueChange={(_, price) =>
                    this.setState(s => ({ form: { ...s.form, price } }), this.validatePriceAndCompareAtPrice)
                  }
                  value={form.price}
                  rightElement={useCAD ? <Tag minimal>CAD</Tag> : undefined}
                  disabled={aProductHasVariants}
                  fill
                />
              </FormGroup>
              <FormGroup label="Compare at price" helperText={helperText}>
                <NullSafeNumericInput
                  min={0}
                  max={1000}
                  minorStepSize={0.01}
                  leftIcon="dollar"
                  stepSize={1}
                  majorStepSize={1}
                  selectAllOnFocus
                  placeholder={this.state.showCompareAtPriceWarning ? '' : 'Use current'}
                  onValueChange={(_, compareAtPrice) =>
                    this.setState(s => ({ form: { ...s.form, compareAtPrice } }), this.validatePriceAndCompareAtPrice)
                  }
                  value={form.compareAtPrice}
                  rightElement={useCAD ? <Tag minimal>CAD</Tag> : undefined}
                  disabled={aProductHasVariants}
                  fill
                />
              </FormGroup>
              <FormGroup label="Duration">
                <ListingDurationSelect
                  value={form.duration}
                  onChange={v => this.setState(s => ({ form: { ...s.form, duration: v } }))}
                  placeholder="Use current"
                  additionalOptions={[{ value: 'INDEFINITE', label: 'Indefinite' }]}
                />
              </FormGroup>
              <FormGroup label="Quantity" helperText={helperText}>
                <NullSafeNumericInput
                  min={1}
                  stepSize={1}
                  majorStepSize={1}
                  minorStepSize={1}
                  selectAllOnFocus
                  placeholder="Use current"
                  onValueChange={(_, quantity) => this.setState(s => ({ form: { ...s.form, quantity } }))}
                  value={form.quantity}
                  disabled={aProductHasVariants}
                  fill
                  clampValueOnBlur
                />
              </FormGroup>
              {config.SHOPIFY_VARIANTS() && (
                <ScheduleTimePickerRelist onChange={this.onChangeScheduleTimePickerRelist} value={form.scheduleTime} />
              )}
            </div>
          )}
          {!this.state.showCompareAtPriceWarning && !this.state.showPriceWarning && <div style={styles.spacingDiv} />}
          {this.state.showCompareAtPriceWarning ? (
            <p style={styles.compareAtPriceTextWarning}>
              Price has been adjusted. Please add a Compare at price greater than the new price or leave blank to remove
              the compare at price option.
            </p>
          ) : null}
          {this.state.showPriceWarning ? (
            <p style={styles.priceTextWarning}>
              Compare at price has been adjusted. Please add a price which is less than the new compare at price.
            </p>
          ) : null}
        </div>

        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <div style={styles.footerActionsContainer}>
              <Button
                loading={loading}
                intent={Intent.PRIMARY}
                onClick={this.maybeCustomSubmit}
                disabled={!!recycledProductId || this.state.isPriceAndCompareAtPriceInvalid}
                style={styles.submitButton}
              >
                {this.props.actionTitle || 'Relist'} {data.length} item{S(data)}
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    )
  }
}
